import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { TaxonomyService } from './taxonomy.service';
import { TaxonomyOverridesService, TaxonomyOverridesMap } from './taxonomy-overrides.service';
import { StudioConfigService } from './studio-config.service';

interface ColorEvent {
  start?: string;
  end?: string;
  appointmentType?: string;
  status?: string | null;
  eventStatus?: string | number | null;
  groupPosted?: boolean;
  eventDetails?: { data?: { eventTypeID?: string | number | null } };
}

const DEFAULT_COLOR = '#666';
const RESCHEDULED_FUTURE_COLOR = '#FF9800';
const GRAPHITE = '#626267';

type TypeVocabulary = 'lesson_type' | 'group_lesson_types' | 'service_type' | 'schedule_type';
type LookupVocabulary = TypeVocabulary | 'lesson_status' | 'service_status';

// Which taxonomy vocabulary each event bundle's field_type references.
// Mirrors target_bundles in the events.<bundle>.field_type field configs.
const BUNDLE_TYPE_VOCAB: Record<string, TypeVocabulary> = {
  lesson: 'lesson_type',
  group_lesson: 'group_lesson_types',
  services: 'service_type',
  schedules: 'schedule_type',
};

@Injectable({
  providedIn: 'root'
})
export class EventColorService {
  private studioTimezone: string = 'America/New_York';

  constructor(
    private taxonomyService: TaxonomyService,
    private taxonomyOverridesService: TaxonomyOverridesService,
    private studioConfigService: StudioConfigService,
  ) {
    this.studioConfigService.getStudioConfig().subscribe(config => {
      this.studioTimezone = config?.['field_studio_timezone']?.[0]?.value || 'America/New_York';
    });
  }

  getEventColor(event: ColorEvent): string {
    if (!event) return DEFAULT_COLOR;

    const overrides = this.taxonomyOverridesService.getOverridesSnapshot();
    const isPast = this.isPastEvent(event);
    const bundle = event.appointmentType;
    const typeId = this.normalizeId(event.eventDetails?.data?.eventTypeID);

    // Cancelled / Rescheduled / NoShowNotCharged take the status term's own
    // color regardless of past/future. Mirrors the pre-check in
    // _amt_dayview_event_details() (section 6) that runs before the lesson-type
    // color logic and short-circuits it for these statuses.
    const statusOverrideColor = this.cancelledOrRescheduledColor(event, bundle, overrides);
    if (statusOverrideColor !== null) {
      return statusOverrideColor;
    }

    let color: string | null = null;

    if (isPast) {
      if (bundle === 'group_lesson') {
        // A fully posted group class (nobody Pending, at least one charged)
        // takes the Showed color, like a posted private lesson. The server
        // decides this in _amt_dayview_group_lesson_posting().
        color = event.groupPosted
          ? this.colorFromTerm(this.SHOWED_TID, false, overrides, 'lesson_status')
          : DEFAULT_COLOR;
      } else if (bundle === 'services') {
        // services use event.field_status as the term id (referencing the
        // service_status vocabulary, NOT lesson_status); event.status carries
        // the name. Looking it up under 'lesson_status' returned null and made
        // every past service gray.
        const statusTermId = this.normalizeId(event.eventStatus);
        color = this.colorFromTerm(statusTermId, true, overrides, 'service_status');
      } else if (bundle === 'lesson') {
        // Start with the lesson type's past color.
        color = this.colorFromTerm(typeId, true, overrides, BUNDLE_TYPE_VOCAB['lesson']);

        // Attendee status overrides the type color for past lessons.
        const statusName = event.status;
        const statusColor = this.colorFromStatusName(statusName, overrides);
        if (statusColor !== null) {
          color = statusColor;
        }
        if (statusName === 'No Showed, Charged' || statusName === 'Canceled Too Late') {
          color = GRAPHITE;
        }
      }
    } else {
      if (bundle === 'lesson') {
        color = this.colorFromTerm(typeId, false, overrides, BUNDLE_TYPE_VOCAB['lesson']);

        // Future-lesson attendee-status overrides.
        const statusName = event.status;
        if (statusName === 'Rescheduled') {
          color = RESCHEDULED_FUTURE_COLOR;
        } else if (statusName === 'Canceled Too Late') {
          color = GRAPHITE;
        }
      } else if (typeId !== null) {
        // For schedules/services/group_lesson, field_type references their own
        // taxonomy (schedule_type, service_type, group_lesson_types). Looking
        // them up under 'lesson_type' returned null and made everything gray.
        const vocab = bundle ? BUNDLE_TYPE_VOCAB[bundle] : undefined;
        if (vocab) {
          color = this.colorFromTerm(typeId, false, overrides, vocab);
        }
      }
    }

    return color ?? DEFAULT_COLOR;
  }

  applyColors<T extends ColorEvent>(events: T[]): T[] {
    if (!Array.isArray(events)) return [];
    return events.map(event => ({
      ...event,
      color: this.getEventColor(event),
    }) as T);
  }

  private isPastEvent(event: ColorEvent): boolean {
    // Flip to "past" the moment the lesson is supposed to have started, not when
    // it ends. Staff want to see the past color as soon as the slot is in
    // progress so they can spot un-actioned lessons at a glance.
    const anchor = event.start ?? event.end;
    if (!anchor) return false;
    // Compare in studio time so the split flips at the studio's local "now", not
    // the browser's. Event timestamps from the backend are wall-clock
    // studio-local times without an offset (e.g. 2026-05-14T15:15:00), so we
    // parse them as moments in the studio zone to anchor them correctly.
    const anchorInStudio = moment.tz(anchor, this.studioTimezone);
    const nowInStudio = moment.tz(this.studioTimezone);
    return anchorInStudio.isBefore(nowInStudio);
  }

  // Status TIDs: 59=Showed, 62=Rescheduled, 63=Cancelled, 61=NoShowNotCharged.
  private readonly SHOWED_TID = '59';
  private readonly CANCELLED_TID = '63';
  private readonly RESCHEDULED_TID = '62';
  private readonly NO_SHOW_NOT_CHARGED_TID = '61';

  private cancelledOrRescheduledColor(
    event: ColorEvent,
    bundle: string | undefined,
    overrides: TaxonomyOverridesMap,
  ): string | null {
    let statusTid: string | null = null;

    // Attendee status name 'Cancelled' (lessons only) maps to the Cancelled
    // lesson_status term.
    if (bundle === 'lesson' && event.status === 'Cancelled') {
      statusTid = this.CANCELLED_TID;
    }

    // Event field_status term id. Sequential checks mirror the PHP so a later
    // match wins, matching _amt_dayview_event_details() section 6.
    const eventStatusTid = this.normalizeId(event.eventStatus);
    if (eventStatusTid === this.RESCHEDULED_TID) statusTid = this.RESCHEDULED_TID;
    if (eventStatusTid === this.CANCELLED_TID) statusTid = this.CANCELLED_TID;
    if (eventStatusTid === this.NO_SHOW_NOT_CHARGED_TID) statusTid = this.NO_SHOW_NOT_CHARGED_TID;

    if (!statusTid) return null;
    // Use the status term's own color (regular, not past), honoring overrides.
    return this.colorFromTerm(statusTid, false, overrides, 'lesson_status');
  }

  private colorFromTerm(
    termId: string | null,
    isPast: boolean,
    overrides: TaxonomyOverridesMap,
    vocabulary: LookupVocabulary,
  ): string | null {
    if (!termId) return null;

    const override = overrides[termId];
    if (override) {
      if (override.hide) return null;
      if (isPast && override.past_event_color) return override.past_event_color;
      if (override.color) return override.color;
    }

    const term = this.findTerm(termId, vocabulary);
    if (!term) return null;
    if (isPast && term.past_color) return term.past_color;
    return term.color ?? null;
  }

  private colorFromStatusName(
    statusName: string | null | undefined,
    overrides: TaxonomyOverridesMap,
  ): string | null {
    if (!statusName) return null;
    const term = (this.taxonomyService.lesson_status || [])
      .find((t: any) => t?.name === statusName);
    if (!term) return null;
    const termId = String(term.id);
    const override = overrides[termId];
    if (override) {
      if (override.hide) return null;
      if (override.color) return override.color;
    }
    return term.color ?? null;
  }

  private findTerm(termId: string, vocabulary: LookupVocabulary): any {
    const list = (this.taxonomyService as any)[vocabulary] as any[];
    if (!Array.isArray(list)) return null;
    return list.find((t) => String(t?.id) === termId) || null;
  }

  private normalizeId(value: string | number | null | undefined): string | null {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  }
}
